import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

/**
 * OBC 同期アラートの通知先 (mst_obc_alert_target) のデータ層フック。
 *
 * OBC データ同期管理ページの「アラート通知先」カードで利用する。
 * 平日 06:00 ルーティン・月次同期ジョブ・貸借対照表更新の結果や異常を、ここで登録した
 * 社員 (Google Chat DM) / スペース (受信 Webhook) へ届ける。
 */

export type AlertTargetKind = 'dm' | 'webhook';
export type AlertLevel = 'info' | 'warning' | 'critical';

export type ObcAlertTarget = {
  id: number;
  target_kind: AlertTargetKind;
  user_id: string | null;
  display_name: string;
  webhook_url: string | null;
  min_level: AlertLevel;
  is_active: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
};

export type ObcAlertTargetInput = {
  id?: number;
  target_kind: AlertTargetKind;
  user_id?: string;
  display_name: string;
  webhook_url?: string;
  min_level: AlertLevel;
  is_active: boolean;
  operator_email?: string;
};

const LIST_URL = ep('get_obc_alert_targets');
const UPSERT_URL = ep('upsert_obc_alert_target');
const DELETE_URL = ep('delete_obc_alert_target');
const TEST_URL = ep('test_obc_alert_target');

export const useObcAlertTargets = () => {
  const [targets, setTargets] = useState<ObcAlertTarget[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(LIST_URL, {});
      if (res.data?.status === 'success') {
        setTargets(res.data.targets || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(async (input: ObcAlertTargetInput) => {
    const res = await axios.post(UPSERT_URL, input);
    if (res.data?.status !== 'success') {
      throw new Error(res.data?.error || '保存に失敗しました');
    }
    await reload();
    return res.data.id as number;
  }, [reload]);

  const remove = useCallback(async (id: number) => {
    const res = await axios.post(DELETE_URL, { id });
    if (res.data?.status !== 'success') {
      throw new Error(res.data?.error || '削除に失敗しました');
    }
    await reload();
  }, [reload]);

  const sendTest = useCallback(async (id: number, operatorEmail?: string) => {
    const res = await axios.post(TEST_URL, { id, operator_email: operatorEmail });
    if (res.data?.status !== 'success') {
      throw new Error(res.data?.error || 'テスト送信に失敗しました');
    }
    return res.data.detail as string;
  }, []);

  return { targets, loading, reload, save, remove, sendTest };
};
